import React from 'react';

import { useFormContext } from 'react-hook-form';

import { Collapse, Divider, Grid, Typography } from '@mui/material';

import ControlledTextField from '../../../components/ControlledTextField';
import ControlledPeoplePicker from '../../../components/ControlledPeoplePicker';
import { CabinEventReport, Employee, EmployeePosition, FormGenerator } from '../../../core/api/clients';
import ControlledAircraftSelect from '../../../components/ControlledAircraftSelect';
import ControlledAirportSelect from '../../../components/ControlledAirportSelect';
import { useCabinEventFieldOptions } from '../../../core/context/CabinEventFieldsOptionsContext';
import { useCabinEventReportData } from '../../../core/context/CabinEventReportDataContext';
import ControlledSelect from '../../../components/ControlledSelect';

import { transformToUtcTime } from '../../../core/textTransforms';

function FlightStep({ visible, readOnly }: { visible: boolean, readOnly?: boolean }) {
  const { airports, aircrafts } = useCabinEventFieldOptions();
  const { cabinEventReport } = useCabinEventReportData();
  const { control, setValue, resetField } = useFormContext<CabinEventReport>();

  const { allowedActions } = useCabinEventReportData();

  return (
    <Collapse in={visible}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <ControlledTextField<CabinEventReport>
            name="flightNumber"
            control={control}
            rules={{ required: cabinEventReport.formGeneratedFrom === FormGenerator.FlightLookup }}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControlledAircraftSelect<CabinEventReport>
            name="aircraftNumber"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={aircrafts ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledAirportSelect<CabinEventReport>
            name="departureAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledAirportSelect<CabinEventReport>
            name="scheduledArrivalAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <ControlledAirportSelect<CabinEventReport>
            name="landingAirport"
            control={control}
            rules={{ required: true }}
            readOnly={readOnly}
            options={airports ?? []}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        {cabinEventReport.pushTime && (
          <React.Fragment>
            <Grid item xs={6} md={3} textAlign="center">
              <Typography variant="caption">OUT: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(cabinEventReport.pushTime)}</Typography>
            </Grid>
            <Grid item xs={6} md={3} textAlign="center">
              <Typography variant="caption">OFF: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(cabinEventReport.offTime)}</Typography>
            </Grid>
            <Grid item xs={6} md={3} textAlign="center">
              <Typography variant="caption">ON: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(cabinEventReport.onTime)}</Typography>
            </Grid>
            <Grid item xs={6} md={3} textAlign="center">
              <Typography variant="caption">IN: </Typography>
              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{transformToUtcTime(cabinEventReport.inTime)}</Typography>
            </Grid>
          </React.Fragment>
        )}
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ControlledPeoplePicker<CabinEventReport>
            groupName="captain"
            name="captain"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['captainId', 'captain', 'captainDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('captain', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('captainDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: cabinEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<CabinEventReport>
            groupName="firstOfficer"
            name="firstOfficer"
            position={EmployeePosition.Flight}
            control={control}
            fieldNames={['firstOfficerId', 'firstOfficer', 'firstOfficerDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('firstOfficer', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('firstOfficerDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: cabinEventReport.formGeneratedFrom !== FormGenerator.Blank }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12}>
          <ControlledPeoplePicker<CabinEventReport>
            groupName="flightAttendant"
            name="flightAttendant"
            position={EmployeePosition.InFlight}
            control={control}
            fieldNames={['flightAttendantId', 'flightAttendant', 'flightAttendantDomicile']}
            onPersonSelected={(employee: Employee | null) => {
              setValue('flightAttendant', employee?.name ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantId', employee?.id ?? null, { shouldDirty: true, shouldTouch: true });
              setValue('flightAttendantDomicile', employee?.location ?? null, { shouldDirty: true, shouldTouch: true });
            }}
            readOnly={readOnly}
            rules={{ required: true }}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <ControlledSelect<CabinEventReport>
            name="cabinObserver"
            control={control}
            menuItems={[
              { name: 'N/A', value: 'N/A' },
              { name: 'Government Agency', value: 'Government Agency' },
              { name: 'Offline', value: 'Offline' },
              { name: 'Air Wisconsin', value: 'Air Wisconsin' },
              { name: 'IOE Student', value: 'IOE Student' },
            ]}
            readOnly={readOnly}
            disableEditPopper={!allowedActions?.edit}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ControlledTextField<CabinEventReport> name="cabinObserverInformation" control={control} readOnly={readOnly} />
        </Grid>
      </Grid>
    </Collapse>
  );
}

export default FlightStep;
